import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Json } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { invokeTriggerTask, describeJobError } from '../lib/triggerTask';

/**
 * Pilot customer onboarding wizard.
 *
 * Collapses pilot-runbook.md §8 (multiple steps) into a single form.
 * Validates each step client-side, then calls onboard_pilot_customer()
 * RPC for the atomic write, then optionally invokes the
 * customer-provisioning-invite Trigger.dev task to send the magic-link.
 *
 * Roadmap-aligned guardrails:
 *   - SLA tier + billing cycle are restricted to schema enums.
 *   - Each row written by the RPC is audited (record_handoff()) so the
 *     onboarding shows up on the chain-of-custody timeline.
 *   - Provider ownership is reviewed by a current administrator after
 *     the exact customer and physical location exist.
 */

type Step = 1 | 2 | 3 | 4 | 5;
type SlaTier = 'standard' | 'premium' | 'enterprise';
type BillingCycle = 'monthly' | 'quarterly' | 'annual';

interface LocationInput {
  name: string;
  type: string;
  city: string;
  state: string;
  timezone: string;
}

export function OnboardPilotPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isStaff = role === 'admin' || role === 'superadmin' || role === 'account_manager';

  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdCustomerId, setCreatedCustomerId] = useState<string | null>(null);
  // The invite is a separate background job: the customer can exist while
  // the invite failed to queue, and that state must be visible.
  const [inviteState, setInviteState] = useState<'skipped' | 'queuing' | 'queued' | 'failed'>('skipped');
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);

  // Step 1 — customer
  const [name, setName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [slaTier, setSlaTier] = useState<SlaTier>('premium');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [mrrCents, setMrrCents] = useState<number>(0);

  // Step 2 — locations
  const [locations, setLocations] = useState<LocationInput[]>([
    { name: '', type: 'headquarters', city: '', state: '', timezone: 'America/New_York' },
  ]);

  // Step 4 — invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sendInvite, setSendInvite] = useState(true);

  if (!isStaff) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted">
        Onboarding requires admin, superadmin, or account-manager role.
      </div>
    );
  }

  function updateLocation(i: number, patch: Partial<LocationInput>) {
    setLocations((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLocation() {
    setLocations((p) => [...p, { name: '', type: 'branch', city: '', state: '', timezone: 'America/New_York' }]);
  }
  function removeLocation(i: number) {
    setLocations((p) => p.filter((_, idx) => idx !== i));
  }

  // ── validation ───────────────────────────────────────────
  function step1Valid(): string | null {
    if (!name.trim()) return 'Customer name is required.';
    if (!accountNumber.trim()) return 'Account number is required.';
    if (mrrCents < 0) return 'MRR cannot be negative.';
    return null;
  }
  function step2Valid(): string | null {
    if (locations.length === 0) return 'At least one location is required.';
    for (const [i, l] of locations.entries()) {
      if (!l.name.trim()) return `Location ${i + 1} needs a name.`;
    }
    return null;
  }
  function step3Valid(): string | null {
    return null;
  }
  function step4Valid(): string | null {
    if (!sendInvite) return null;
    if (!inviteEmail.trim()) return 'Invite email is required (or untick "send invite").';
    if (!inviteEmail.includes('@')) return 'Invite email looks invalid.';
    return null;
  }

  /**
   * Queues the customer-provisioning-invite task. The task expects the
   * snake_case InviteInput contract (customer_id / email / full_name / role);
   * the Edge Function identifies the inviting staff member itself.
   */
  async function queueInvite(customerId: string) {
    const email = inviteEmail.trim().toLowerCase();
    setInviteState('queuing');
    setInviteMessage(null);
    try {
      const result = await invokeTriggerTask(
        'customer-provisioning-invite',
        {
          customer_id: customerId,
          email,
          full_name: inviteName.trim() || undefined,
          role: 'viewer',
          is_primary_contact: true,
        },
        { idempotencyKey: `invite-${customerId}-${email}` },
      );
      setInviteState('queued');
      setInviteMessage(`Magic-link invite to ${email} queued${result.runId ? ` (run ${result.runId})` : ''}.`);
    } catch (e) {
      setInviteState('failed');
      setInviteMessage(describeJobError(e));
    }
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const locationsPayload = locations.map((l) => ({
        name: l.name,
        type: l.type,
        timezone: l.timezone,
        address: { city: l.city, state: l.state },
      }));

      const { data: customerId, error: rpcErr } = await supabase.rpc('onboard_pilot_customer', {
        p_name: name.trim(),
        p_account_number: accountNumber.trim(),
        p_sla_tier: slaTier,
        p_billing_cycle: billingCycle,
        p_mrr_cents: mrrCents,
        p_locations: locationsPayload as unknown as Json,
        p_auvik_tenants: [],
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!customerId) throw new Error('Onboarding returned no customer id');
      setCreatedCustomerId(customerId);

      if (sendInvite) {
        await queueInvite(customerId);
      } else {
        setInviteState('skipped');
        setInviteMessage(null);
      }

      setStep(5);
    } catch (e) {
      setError(`Could not onboard this customer: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-text">Onboard customer</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Create the customer, physical locations, and first portal invite. An administrator reviews provider ownership afterward using these exact records. Each submission writes an append-only audit handoff.
        </p>
      </div>

      {/* Step indicator */}
      <ol className="flex items-center justify-between text-xs">
        {(['Customer', 'Locations', 'Provider review', 'Invite', 'Done'] as const).map((label, i) => {
          const active = step === ((i + 1) as Step);
          const done = step > ((i + 1) as Step);
          return (
            <li key={label} className="flex flex-col items-center gap-1 flex-1">
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium ${
                  done
                    ? 'bg-success text-white'
                    : active
                    ? 'bg-primary text-text-inverse'
                    : 'bg-surface-3 text-text-muted'
                }`}
              >
                {i + 1}
              </span>
              <span className={done || active ? 'text-text' : 'text-text-muted'}>{label}</span>
            </li>
          );
        })}
      </ol>

      <div className="rounded-lg border border-border bg-surface p-5">
        {step === 1 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-text">Customer details</h2>
            <Field label="Customer name">
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>
            <Field label="Account number (Zoho)">
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="SLA tier">
                <select value={slaTier} onChange={(e) => setSlaTier(e.target.value as SlaTier)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </Field>
              <Field label="Billing cycle">
                <select value={billingCycle} onChange={(e) => setBillingCycle(e.target.value as BillingCycle)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </Field>
            </div>
            <Field label="MRR (USD cents)">
              <input
                type="number"
                min={0}
                value={mrrCents}
                onChange={(e) => setMrrCents(Number.parseInt(e.target.value, 10) || 0)}
                className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text">Locations</h2>
              <button onClick={addLocation} className="text-xs text-primary hover:underline">
                + Add location
              </button>
            </div>
            {locations.map((loc, i) => (
              <div key={i} className="rounded border border-border p-3 space-y-2 bg-surface-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-faint">Location {i + 1}</span>
                  {locations.length > 1 && (
                    <button onClick={() => removeLocation(i)} className="text-xs text-error hover:underline min-h-[44px] sm:min-h-0">
                      Remove
                    </button>
                  )}
                </div>
                <Field label="Name">
                  <input value={loc.name} onChange={(e) => updateLocation(i, { name: e.target.value })} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Type">
                    <select value={loc.type} onChange={(e) => updateLocation(i, { type: e.target.value })} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary">
                      <option value="headquarters">Headquarters</option>
                      <option value="branch">Branch</option>
                      <option value="retail">Retail</option>
                      <option value="hospitality">Hospitality</option>
                      <option value="warehouse">Warehouse</option>
                    </select>
                  </Field>
                  <Field label="Timezone">
                    <input value={loc.timezone} onChange={(e) => updateLocation(i, { timezone: e.target.value })} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                  </Field>
                  <Field label="City">
                    <input value={loc.city} onChange={(e) => updateLocation(i, { city: e.target.value })} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                  </Field>
                  <Field label="State">
                    <input value={loc.state} onChange={(e) => updateLocation(i, { state: e.target.value })} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-text">Provider ownership review follows creation</h2>
              <p className="text-xs text-text-muted">
                After creating this customer and its locations, an administrator selects an exact discovered Auvik or UniFi provider ID in Integrations, chooses the existing customer and location, and records a review reason. Monitoring registration then uses that reviewed assignment.
              </p>
            </div>
            <p className="rounded border border-border p-3 text-xs text-text-muted">Continue to create the customer and locations. This onboarding step does not assign a monitoring provider.</p>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-text">Customer admin invite</h2>
            <label className="flex items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="rounded border-border"
              />
              Send a magic-link portal invite (viewer role) when the customer is created
            </label>
            {sendInvite && (
              <>
                <Field label="Invitee email">
                  <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                </Field>
                <Field label="Full name (optional)">
                  <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary" />
                </Field>
              </>
            )}
          </div>
        )}

        {step === 5 && createdCustomerId && (
          <div className="space-y-3 text-center">
            <h2 className="text-base font-semibold text-success">Customer created</h2>
            <p className="text-sm text-text">
              <span className="font-medium">{name}</span> is created with{' '}
              {locations.length} location{locations.length === 1 ? '' : 's'}.
            </p>
            <p className="text-sm text-text-muted">The customer and locations are ready for exact provider ownership review in Integrations.</p>
            {inviteState === 'queued' && (
              <p role="status" className="text-sm text-success break-words">{inviteMessage}</p>
            )}
            {inviteState === 'queuing' && (
              <p role="status" className="text-sm text-text-muted">Queuing the portal invite…</p>
            )}
            {inviteState === 'failed' && (
              <div role="alert" className="rounded-md border border-error/40 bg-error-soft p-3 text-sm text-error text-left space-y-2">
                <p className="break-words">The customer was created, but the portal invite was not queued: {inviteMessage}</p>
                <button
                  type="button"
                  onClick={() => createdCustomerId && void queueInvite(createdCustomerId)}
                  className="min-h-[44px] sm:min-h-[36px] px-3 rounded-md border border-error/40 bg-surface text-xs font-medium text-error hover:bg-surface-strong"
                >
                  Retry invite
                </button>
                <p className="text-xs">You can also invite from Customer 360 or the Provisioning page later.</p>
              </div>
            )}
            {inviteState === 'skipped' && (
              <p className="text-sm text-text-muted">No portal invite was sent. Invite a contact from Customer 360 when ready.</p>
            )}
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={() => navigate(`/customers/${createdCustomerId}`)}
                className="px-4 py-2 text-sm rounded bg-primary text-text-inverse"
              >
                Open customer
              </button>
              {(role === 'admin' || role === 'superadmin') && <button type="button" onClick={() => navigate('/integrations/auvik')} className="px-4 py-2 text-sm rounded border border-border text-text">Review provider ownership</button>}
              <button
                onClick={() => {
                  setStep(1);
                  setCreatedCustomerId(null);
                  setName('');
                  setAccountNumber('');
                  setMrrCents(0);
                  setLocations([{ name: '', type: 'headquarters', city: '', state: '', timezone: 'America/New_York' }]);
                  setInviteEmail('');
                  setInviteName('');
                  setInviteState('skipped');
                  setInviteMessage(null);
                }}
                className="px-4 py-2 text-sm rounded border border-border text-text"
              >
                Onboard another
              </button>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-xs text-error break-words">{error}</p>
        )}

        {step < 5 && (
          <div className="flex justify-between mt-5">
            <button
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}
              disabled={step === 1}
              className="text-xs text-text-muted hover:text-text disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={() => {
                const validator = step === 1 ? step1Valid : step === 2 ? step2Valid : step === 3 ? step3Valid : step4Valid;
                const err = validator();
                if (err) { setError(err); return; }
                setError(null);
                if (step === 4) { void submit(); }
                else setStep((s) => ((s + 1) as Step));
              }}
              disabled={submitting}
              className="px-4 py-1.5 text-xs rounded bg-primary text-text-inverse disabled:opacity-60"
            >
              {step === 4 ? (submitting ? 'Creating…' : 'Onboard') : 'Next'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
